import { Col, Row, Typography } from "antd"
import { useTitle } from "hooks/useTitle"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { getGuideTrip } from "store/trips"
import { buildPreviewPayload } from "../mappers/toApiPayload"
import { useWizardContext } from "../WizardContext"
import { updateTrip } from "store/trips"
import { PreviewAbout } from "pagesV2/pro/Experiences/ListingPreview/PreviewAbout"
import { PreviewAddress } from "pagesV2/pro/Experiences/ListingPreview/PreviewAddress"
import { PreviewGuide } from "pagesV2/pro/Experiences/ListingPreview/PreviewGuide"
import { PreviewMediaListing } from "pagesV2/pro/Experiences/ListingPreview/PreviewMedia"
import { PreviewMeetingLocation } from "pagesV2/pro/Experiences/ListingPreview/PreviewMeetingLocation"
import BookingDetailCard from "pagesV2/pro/Embedded/ExperienceBooking/BookingDetailCard"
import ActionsPanel from "pagesV2/pro/Experiences/ListingPreview/ActionsPanel"
import styles from "pagesV2/pro/Experiences/ListingPreview/ExperiencesPreview.module.css"
import { routes } from "router"

const { Title } = Typography

/**
 * Preview step — read-only preview of the experience before publishing.
 *
 * Fetches the trip with preview flags for accurate pricing display.
 * Registers a save handler that marks wizard_completed=true.
 *
 * When accessed via ExperienceCard preview button (readOnlyPreview=true),
 * shows ActionsPanel (Edit/Share/Deactivate/Delete) instead of BookingDetailCard.
 * The `route` param (published|deactivated) controls which actions are shown
 * and where navigation goes after delete/deactivate — matching the old wizard behavior.
 */
export const PreviewStep = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { stepSaveHandlersRef } = useWizardContext()
  const trip = useSelector(modelSelector("trip"))

  const isPreviewMode = searchParams.get("readOnlyPreview") === "true"
  const routeType = searchParams.get("route")

  useLoaderWatchers({ watchers: ["trips.trip"] })
  useTitle(trip.title)

  useEffect(() => {
    if (id) {
      dispatch(getGuideTrip(id, true, true))
    }
  }, [id, dispatch])

  // Register save handler — marks wizard as complete when user publishes
  useEffect(() => {
    stepSaveHandlersRef.current["preview"] = async () => {
      const formData = buildPreviewPayload()
      await dispatch(updateTrip(id, formData))
    }
    return () => {
      delete stepSaveHandlersRef.current["preview"]
    }
  }, [dispatch, id, stepSaveHandlersRef])

  const handleEdit = () => {
    navigate(routes.proEditExperienceV2Path({ id: trip?.id }))
  }

  const { title } = trip

  return (
    <>
      <div style={{ position: "relative", paddingRight: "100px" }}>
        <Title level={2} style={{ lineHeight: 1, marginBottom: 0 }}>
          {title}
        </Title>
      </div>
      <PreviewAddress trip={trip} t={t} />
      <PreviewMediaListing trip={trip} />
      <Row style={{ paddingBottom: 150 }}>
        <Col span={24} lg={24} xl={15} style={{ marginBottom: "20px" }}>
          <PreviewAbout trip={trip} t={t} />
          <PreviewMeetingLocation trip={trip} t={t} />
          <PreviewGuide trip={trip} t={t} />
        </Col>
        <Col span={24} lg={24} xl={9} className={styles.formCard}>
          {isPreviewMode ? (
            <ActionsPanel trip={trip} onEdit={handleEdit} routeType={routeType} />
          ) : (
            <BookingDetailCard bookNow={false} trip={trip} bookingButtonVisible />
          )}
        </Col>
      </Row>
    </>
  )
}
